// LiveWorkoutRunner — T-016 Part A. The screen that decides whether the
// product works. Walks the user through N sets of one exercise, writing one
// Dexie row per completed set via the existing `createWorkout` helper from
// T-006, with the local write and its syncQueue entry in one transaction.
//
// ADR-0013 D3: an abandoned start is either discarded (zero sets) or kept
// as *unfinished* (one or more sets, completed_at stays null). The runner
// owns the unmount-time cleanup: with zero sets logged we tombstone the
// workout_logs row; with one or more sets we leave the row in place so
// the user can resume it from history. The in-app **End workout** control
// offers an explicit Keep/Discard sheet for the ≥1-set case.

import { useCallback, useEffect, useRef, useState } from "react";
import type { JSX } from "react";
import { useI18n } from "../../i18n";
import {
  finishWorkout,
  getDb,
  getOwner,
  logSet,
  softDeleteWorkout,
  startWorkout,
} from "../../services/db";
import type { LogEntryRow, WorkoutLogRow } from "../../services/db";
import { newId } from "../../services/ids";
import { setWorkoutActive } from "../../hooks/workoutActive";
import {
  countTick as audioCountTick,
  levelUp as audioLevelUp,
  restEnd as audioRestEnd,
  restStart as audioRestStart,
  setDone as audioSetDone,
  unlock as audioUnlock,
  startBlip as audioStartBlip,
} from "../../services/audioCues";
import type { Exercise } from "../../types/calisthenics";
import type { LevelEarnedDetail } from "../app/LevelEarnedToast";
import type { ProgressionStandard } from "../../types/calisthenics";
import { QuickLogger } from "./QuickLogger";
import { RestTimer } from "./RestTimer";
import { StaticHoldTimer } from "./StaticHoldTimer";
import { ExerciseMedia } from "../common/ExerciseMedia";
import {
  aggregateProgressWithStandards,
  TIER_SETS,
} from "../../utils/progressionEngine";
import type { ProgressionStatus } from "../../utils/progressionEngine";

// T-098: voice control (the speech adapter, the command wiring and
// the indicator) was removed from the whole app on the human's instruction.
// The keypad (T-095) is the only direct path to a number.

export interface LiveWorkoutRunnerProps {
  exercise: Exercise;
  setCount?: number;
  restSeconds?: number;
  /** When set, the runner resumes the given workout_logs row at the next
   *  set number, instead of starting a fresh workout. ADR-0013 D3 §3. */
  resumeWorkoutId?: string;
  /** Called after the runner is done (kept as unfinished, discarded, or
   *  completed). The route handler can navigate away. */
  onResolved?: () => void;
}

type Phase = "ready" | "work" | "rest" | "complete";

/** ADR-0013 D4: the rep field never pre-fills with the standard. It starts
 *  at the last reps logged for this exercise if any, else 0. The target
 *  is shown as a label beside the field, not as a value. */
async function defaultReps(
  exercise: Exercise,
  owner: string | null,
): Promise<number> {
  if (!owner) return 0;
  const db = getDb();
  const entries = await db.log_entries
    .where("owner")
    .equals(owner)
    .and((r) => !r.deletedAt && r.exercise === exercise.id)
    .toArray();
  if (entries.length === 0) return 0;
  // Newest first.
  entries.sort((a, b) => (a.updatedAt < b.updatedAt ? 1 : -1));
  return entries[0]!.reps ?? 0;
}

function isHold(exercise: Exercise): boolean {
  return (exercise.standard.holdSeconds ?? 0) > 0;
}

/** The hold seconds for the next tier above `best` (T-095 §3.5).
 *  Mirrors the reps path: `beginnerHoldSeconds` for fresh
 *  exercises, `intermediateHoldSeconds` once the beginner hold
 *  is cleared, `holdSeconds` for mastery. Falls back to the
 *  mastery seconds if the intermediate is absent in the seed. */
function targetSecondsFor(
  best: ProgressionStatus,
  std: ProgressionStandard,
): number {
  const next = nextTierFor(best);
  if (next === "beginner") {
    return std.beginnerHoldSeconds ?? std.holdSeconds ?? 0;
  }
  if (next === "intermediate") {
    return std.intermediateHoldSeconds ?? std.holdSeconds ?? 0;
  }
  return std.holdSeconds ?? 0;
}

/** Tier directly above the user's best on this exercise. Used for the
 *  "target" label: "Ziel: 3 × 10 · Beginner". ADR-0013 D6. */
function nextTierFor(best: ProgressionStatus): ProgressionStatus {
  switch (best) {
    case "none":
      return "beginner";
    case "beginner":
      return "intermediate";
    case "intermediate":
      return "mastery";
    case "mastery":
    default:
      return "mastery";
  }
}

export function LiveWorkoutRunner({
  exercise,
  setCount = TIER_SETS.mastery,
  restSeconds = 90,
  resumeWorkoutId,
  onResolved,
}: LiveWorkoutRunnerProps): JSX.Element {
  const { t, locale } = useI18n();
  const [phase, setPhase] = useState<Phase>(resumeWorkoutId ? "work" : "ready");
  const [currentSet, setCurrentSet] = useState(1);
  const [reps, setReps] = useState<number>(0);
  const [workoutId, setWorkoutId] = useState<string | null>(
    resumeWorkoutId ?? null,
  );
  // Stashed `calicool:level-earned` detail. The toast suppresses
  // itself while a workout is active; we want the toast to appear
  // AFTER the workout is over, not during the work phase.
  // Declared here (before any effect that reads it) to avoid the
  // TDZ from a useEffect above this hook.
  const [levelEarned, setLevelEarned] =
    useState<LevelEarnedDetail | null>(null);
  const [saveError, setSaveError] = useState(false);
  const [restFlash, setRestFlash] = useState(false);
  const [leaveSheetOpen, setLeaveSheetOpen] = useState(false);

  // ADR-0013 D4: load the last-logged rep count for this exercise and seed
  // `reps`. Done once on mount; the runner does not watch db for updates.
  const ownerIdRef = useRef<string | null>(null);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const owner = getOwner();
      ownerIdRef.current = owner;
      try {
        const n = await defaultReps(exercise, owner);
        if (!cancelled) setReps(n);
      } catch {
        // The db may close between mount and effect run (tests);
        // production never closes mid-mount. No-op.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [exercise]);

  // ADR-0013 D3: unmount-time cleanup. With zero sets we tombstone the
  // workout_logs row (start → leave, ADR-0013 D3 first bullet). With
  // one or more sets we leave it as unfinished (ADR-0013 D3 second
  // bullet). The startup sweep handles the edge case where unmount
  // never runs (browser crash, OS kill).
  const loggedSetsRef = useRef(0);
  // T-090 R-25 H3 / T-096: on a resume mount the set count is unknown
  // until the async entries query resolves. Until then neither the
  // unmount cleanup nor `endWorkout` may act on `loggedSetsRef === 0`
  // — a workout the person already started must never disappear
  // because a read had not finished (StrictMode double-mount, Back
  // or End before the query resolves). `resumeLoadedRef` flips true
  // when the query has resolved; a non-resume mount has nothing to
  // wait for. (T-096 §3: the `resumeEntryCount` prop that was meant
  // to pre-fill this was never passed by any caller and is gone.)
  const resumeLoadedRef = useRef(!resumeWorkoutId);
  useEffect(() => {
    return () => {
      const wid = workoutId;
      const phaseAtUnmount = phase;
      if (!wid) return;
      if (phaseAtUnmount === "complete") return;
      // Wait until the entries query has resolved before we act on
      // the set count. Without this guard StrictMode's first cleanup
      // tombstones a workout that has sets on a real device, and any
      // Back-then-Forward before the entries query resolves does the
      // same in production.
      if (!resumeLoadedRef.current) return;
      if (loggedSetsRef.current === 0) {
        Promise.resolve(softDeleteWorkout(wid)).catch(() => undefined);
      }
      // else: keep the row as-is (completed_at stays null), history
      // shows it as "unfinished" with the sets logged. The user can
      // resume or discard from there.
    };
  }, [workoutId, phase]);

  const begin = useCallback(async () => {
    audioUnlock();
    audioStartBlip();
    try {
      const id = await startWorkout();
      setWorkoutId(id);
      setSaveError(false);
      setPhase("work");
    } catch {
      setSaveError(true);
    }
  }, []);

  // Resume path: the runner was mounted with resumeWorkoutId; jump to
  // the next set number and load the most recent rep count for the
  // exercise. The workout_logs row is already on disk and we just add
  // more log_entries to it.
  useEffect(() => {
    if (!resumeWorkoutId) return;
    let cancelled = false;
    (async () => {
      let entries: LogEntryRow[];
      try {
        const db = getDb();
        entries = (await db.log_entries
          .where("workout")
          .equals(resumeWorkoutId)
          .and((r) => !r.deletedAt)
          .toArray()) as LogEntryRow[];
      } catch {
        // The db closed between mount and effect (tests, teardown).
        // Leave `resumeLoadedRef` false: the unmount cleanup then
        // never tombstones a resumed workout on an unknown count.
        return;
      }
      if (cancelled) return;
      const next = entries.length + 1;
      setCurrentSet(next);
      loggedSetsRef.current = entries.length;
      // The entries query has resolved; the unmount cleanup is now
      // safe to act on `loggedSetsRef`. R-25 H3.
      resumeLoadedRef.current = true;
    })();
    return () => {
      cancelled = true;
    };
  }, [resumeWorkoutId]);

  const endRest = useCallback(() => {
    audioUnlock();
    audioRestEnd();
    setRestFlash(true);
    window.setTimeout(() => setRestFlash(false), 120);
    setPhase("work");
  }, []);

  const completeSet = useCallback(
    async (loggedReps: number) => {
      audioUnlock();
      audioSetDone();
      const wid = workoutId;
      if (!wid) {
        setSaveError(true);
        return;
      }
      // Increment BEFORE awaiting the write so the unmount cleanup
      // counts an in-flight set as logged (the row stays as
      // unfinished, not discarded). The race window is exactly the
      // tap-then-back case the ADR was written for.
      loggedSetsRef.current += 1;
      try {
        await logSet({
          workoutId: wid,
          exerciseId: exercise.id,
          set_number: currentSet,
          reps: isHold(exercise) ? undefined : loggedReps,
          hold_seconds: isHold(exercise) ? loggedReps : undefined,
        });
      } catch {
        // Stay in the work phase so the user can retry; do not advance.
        setSaveError(true);
        loggedSetsRef.current -= 1;
        return;
      }
      setSaveError(false);
      // ADR-0013 D4 rev 3: seed the next set with the reps just
      // logged, not 0. The first set seeds from `lastRepsFor` (or 0
      // if no history). Sets 2+ are usually the same number or
      // one or two off, so the seed is a real ergonomic win and is
      // not the same as the pre-fill bug (the pre-fill used
      // `standard.beginnerReps` regardless of what the person just
      // did). The set-done button is still disabled while reps===0
      // if the seed is 0.
      setReps(loggedReps);
      if (currentSet >= setCount) {
        try {
          await finishWorkout(wid);
        } catch {
          setSaveError(true);
          return;
        }
        // ADR-0013 D5: write the synced tier row for the exercise(s) in
        // this workout. Toast `level.earned` once per risen tier.
        // The workout is already complete on disk (finishWorkout above);
        // a failure here is a sync-layer problem, not a reason to leave
        // the person stuck in the work phase, and the tree's derived
        // fallback (ADR-0013 D5) still shows the tier. So: complete
        // anyway, no toast, and never let the rejection escape
        // `completeSet` (it is called as `void completeSet(...)`).
        let writeResult: Awaited<ReturnType<typeof writeUserProgressForWorkout>> = null;
        try {
          writeResult = await writeUserProgressForWorkout(exercise);
        } catch {
          writeResult = null;
        }
        audioLevelUp();
        // Stash the detail for dispatch. The dispatch fires from
        // an effect that watches `phase` so it lands AFTER React
        // commits the phase change and the `setWorkoutActive`
        // effect has run. The toast suppresses itself while a
        // workout is active; we want the toast to appear once
        // the workout is over, not during the work phase.
        if (writeResult && tierRank(writeResult.tier) > tierRank(writeResult.wasTier)) {
          setLevelEarned({
            exerciseId: exercise.id,
            tier: writeResult.tier,
            was: writeResult.wasTier,
            reps: writeResult.reps,
            seconds: writeResult.seconds,
            isHold: isHoldFromStandard(exercise.standard),
            sets: TIER_SETS[writeResult.tier as Exclude<typeof writeResult.tier, "none">],
          });
        }
        setPhase("complete");
        return;
      }
      audioRestStart();
      setCurrentSet((s) => s + 1);
      setPhase("rest");
    },
    [currentSet, setCount, exercise, workoutId],
  );

  // T-095 §3: StaticHoldTimer now passes the held seconds. The
  // previous `onHoldComplete` called `completeSet(0)`, which
  // logged every hold as `hold_seconds: 0` and made the beginner
  // hold tier (and every tier above) unreachable. The fix: log
  // what the timer measured, so `tierOfWorkout` can award hold
  // tiers from real seconds.
  const onHoldConfirm = useCallback(
    (seconds: number) => {
      completeSet(seconds);
    },
    [completeSet],
  );

  // **End workout** control. Zero sets → tombstone + resolve. ≥1 set →
  // confirm sheet with Keep / Discard. ADR-0013 D3.
  const endWorkout = useCallback(() => {
    audioUnlock();
    const wid = workoutId;
    if (!wid) {
      onResolved?.();
      return;
    }
    // T-096 §1: only a KNOWN zero-set workout is discarded. On a resume
    // mount the count is unknown until the entries query resolves;
    // treat that as "has sets" and offer Keep/Discard instead. This was
    // the last path that could tombstone a workout with sets.
    if (resumeLoadedRef.current && loggedSetsRef.current === 0) {
      void softDeleteWorkout(wid)
        .catch(() => undefined)
        .finally(() => {
          onResolved?.();
        });
      return;
    }
    setLeaveSheetOpen(true);
  }, [workoutId, onResolved]);

  const keepAsUnfinished = useCallback(() => {
    setLeaveSheetOpen(false);
    onResolved?.();
  }, [onResolved]);

  const discardWorkout = useCallback(() => {
    const wid = workoutId;
    setLeaveSheetOpen(false);
    if (!wid) {
      onResolved?.();
      return;
    }
    void softDeleteWorkout(wid)
      .catch(() => undefined)
      .finally(() => {
        onResolved?.();
      });
  }, [workoutId, onResolved]);

  // Wake lock for the duration of the workout.
  useEffect(() => {
    if (phase === "complete") return;
    type Lock = { release: () => Promise<void> };
    type Sentinel = { wakeLock?: { request: (type: "screen") => Promise<Lock> } };
    const nav = navigator as unknown as Sentinel;
    if (!nav.wakeLock) return;
    let lock: Lock | null = null;
    let cancelled = false;
    const acquire = async () => {
      try {
        lock = await nav.wakeLock!.request("screen");
      } catch {
        lock = null;
      }
    };
    void acquire();
    const onVisible = () => {
      if (!cancelled && document.visibilityState === "visible") {
        audioUnlock();
        void acquire();
      }
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      cancelled = true;
      document.removeEventListener("visibilitychange", onVisible);
      void lock?.release();
    };
  }, [phase]);

  // ADR-0007 D3: PWA update deferral is gated on real workout state.
  // Set the flag while the runner owns a session (ready / work / rest);
  // clear it on `complete` and on unmount so the toast can reappear.
  useEffect(() => {
    const active = phase === "ready" || phase === "work" || phase === "rest";
    setWorkoutActive(active);
    if (!active) return;
    return () => setWorkoutActive(false);
  }, [phase]);

  // Dispatch the stashed `level.earned` detail AFTER the workout
  // is over. The toast suppresses itself while a workout is
  // active; watching `phase` keeps the dispatch in the same
  // React commit cycle as the activity flip, so the toast
  // actually renders.
  useEffect(() => {
    if (!levelEarned) return;
    if (phase !== "complete") return;
    window.dispatchEvent(
      new CustomEvent("calicool:level-earned", { detail: levelEarned }),
    );
    setLevelEarned(null);
  }, [phase, levelEarned]);


  // Compute the target label: "Ziel: 3 × 10 · Beginner" for the next tier.
  // ADR-0013 D6.
  const [nextTier, setNextTier] = useState<ProgressionStatus>("beginner");
  // Mirror of `nextTier` for the hold path (T-095 §3.5) so the
  // StaticHoldTimer can show the next tier's hold seconds as its
  // target. Defaults to "beginner" (matches nextTier). */
  const [bestTier, setBestTier] = useState<ProgressionStatus>("none");
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const db = getDb();
      const owner = getOwner();
      if (!owner) return;
      try {
        const workouts = await db.workout_logs
          .where("owner")
          .equals(owner)
          .and((r) => !r.deletedAt && r.completed_at != null)
          .toArray();
        const entries = await db.log_entries
          .where("owner")
          .equals(owner)
          .and((r) => !r.deletedAt)
          .toArray();
        const stdMap: Record<string, ProgressionStandard> = {
          [exercise.id]: exercise.standard,
        };
        const progress = aggregateProgressWithStandards(
          workouts,
          entries,
          stdMap,
        );
        const best = progress[exercise.id]?.bestTier ?? "none";
        if (!cancelled) {
          setNextTier(nextTierFor(best));
          setBestTier(best);
        }
      } catch {
        // db closed mid-effect; nothing to surface.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [exercise]);

  return (
    <section
      data-testid="live-workout-runner"
      aria-label="live-workout"
      className="mx-auto flex w-full max-w-[640px] flex-col gap-6 px-4 py-6"
      style={{ minHeight: "min(90vh, 800px)" }}
    >
      <header className="flex flex-col gap-2 text-center">
        <div className="flex items-center justify-center gap-1.5" aria-label="Set Progress">
          {Array.from({ length: setCount }, (_, i) => i + 1).map((s) => (
            <span
              key={s}
              className={
                "inline-flex items-center justify-center px-3 py-1 font-subhead text-xs uppercase tracking-[0.08em] transition-all " +
                (s === currentSet
                  ? "border-2 border-[var(--color-lime)] bg-[var(--color-lime)] font-bold text-[var(--color-void)] shadow-[0_0_12px_rgba(204,255,0,0.35)]"
                  : s < currentSet
                    ? "border border-[var(--color-lime)]/40 bg-[var(--color-slab-2)] text-[var(--color-lime)]"
                    : "border border-[var(--color-line)] bg-[var(--color-slab)] text-[var(--color-ink-mute)]")
              }
              style={{ borderRadius: "var(--radius-sharp)", minHeight: "28px" }}
            >
              {t("workoutRunner.setNumbered", { number: s })}
            </span>
          ))}
        </div>
        <p
          data-testid="set-counter"
          className="font-subhead text-xs uppercase tracking-[0.06em] text-[var(--color-ink-mute)]"
        >
          {t("workoutRunner.setOf", { current: currentSet, total: setCount })}
        </p>
        <h1
          className="font-display text-3xl uppercase leading-[1.1] tracking-[0.02em] text-[var(--color-ink-hi)] sm:text-4xl"
          style={{ minHeight: 64 }}
        >
          {exercise.name[locale]}
        </h1>
      </header>

      <div className="flex-1">
        {phase === "ready" && (
          <div className="flex flex-col gap-4">
            {/* ExerciseMedia owns its own mediaBroken state and is keyed by
                exercise.id, so the mediaBroken flag really does reset on
                every exercise change (see ExerciseMedia.tsx for the long
                version). */}
            <ExerciseMedia
              key={exercise.id}
              exercise={exercise}
              locale={locale}
              imgClassName="max-h-48 w-auto rounded-[var(--radius-sharp)] object-contain"
            />
            <button
              type="button"
              data-testid="workout-start"
              onClick={begin}
              className="w-full font-display uppercase tracking-[0.02em] text-[var(--color-void)]"
              style={{
                minHeight: "100px",
                fontSize: 32,
                backgroundColor: "var(--color-lime)",
                borderRadius: "var(--radius-sharp)",
              }}
            >
              {t("workoutRunner.actions.start")}
            </button>
          </div>
        )}

        {phase === "work" && (
          <>
            {saveError && (
              <div
                data-testid="save-error"
                className="rounded-[var(--radius-sharp)] border border-[var(--color-rust)] bg-[var(--color-rust)]/10 p-4 text-center"
              >
                <p className="font-sans text-sm text-[var(--color-ink-hi)]">
                  {t("workoutRunner.saveFailed")}
                </p>
                <button
                  type="button"
                  onClick={() => void completeSet(reps)}
                  data-testid="save-retry"
                  className="mt-2 font-subhead text-sm uppercase tracking-[0.06em] text-[var(--color-lime)]"
                >
                  {t("common.retry")}
                </button>
              </div>
            )}
            {isHold(exercise) ? (
              <StaticHoldTimer
                targetSeconds={targetSecondsFor(bestTier, exercise.standard)}
                onConfirm={onHoldConfirm}
                autoStart
                tierLabel={t(`tree.${nextTier}`)}
              />
            ) : (
              <QuickLogger
                targetReps={
                  nextTier === "mastery"
                    ? exercise.standard.masteryReps
                    : nextTier === "intermediate"
                      ? exercise.standard.intermediateReps
                      : exercise.standard.beginnerReps
                }
                completedSets={currentSet - 1}
                totalSets={setCount}
                reps={reps}
                onRepsChange={setReps}
                onDone={() => void completeSet(reps)}
                canConfirm={reps > 0}
                tierLabel={t(`tree.${nextTier}`)}
              />
            )}
          </>
        )}

        {(phase === "work" || phase === "rest") && (
          <button
            type="button"
            data-testid="workout-end"
            onClick={endWorkout}
            className="mt-4 w-full border border-[var(--color-line-strong)] px-4 py-3 font-subhead text-sm uppercase tracking-[0.06em] text-[var(--color-ink)] hover:bg-[var(--color-slab-2)]"
            style={{ borderRadius: "var(--radius-sharp)", minHeight: "44px" }}
          >
            {t("workoutRunner.end")}
          </button>
        )}

        {phase === "rest" && (
          <RestTimer
            seconds={restSeconds}
            onComplete={endRest}
            onTick={(n) => audioCountTick(n as 1 | 2 | 3)}
            onSkip={endRest}
          />
        )}

        {phase === "complete" && (
          <div
            data-testid="workout-complete"
            className="flex flex-col items-center gap-4 text-center"
          >
            <h2 className="font-display text-3xl uppercase tracking-[0.02em] text-[var(--color-lime)]">
              {t("workoutRunner.complete")}
            </h2>
            <p className="font-sans text-base text-[var(--color-ink)]">
              {exercise.name[locale]}
            </p>
            <button
              type="button"
              onClick={() => {
                onResolved?.();
              }}
              className="mt-4 border border-[var(--color-lime)] bg-[var(--color-lime)] px-6 py-3 font-subhead text-sm font-bold uppercase tracking-[0.06em] text-[var(--color-void)]"
              style={{ borderRadius: "var(--radius-sharp)" }}
            >
              {t("common.returnHome")}
            </button>
          </div>
        )}
      </div>

      {restFlash && (
        <div
          data-testid="rest-end-flash"
          aria-hidden="true"
          className="pointer-events-none fixed inset-0 z-50"
          style={{
            backgroundColor: "var(--color-lime)",
            opacity: 0.35,
            transition: "opacity 120ms linear",
          }}
        />
      )}
      {leaveSheetOpen && (
        <div
          data-testid="leave-sheet"
          role="dialog"
          aria-modal="true"
          aria-label={t("workoutRunner.leave.title")}
          className="fixed inset-0 z-50 flex items-end justify-center bg-[var(--color-void)]/80 sm:items-center"
          onClick={() => setLeaveSheetOpen(false)}
        >
          <div
            className="w-full max-w-[640px] border border-[var(--color-line)] bg-[var(--color-slab)] p-6 shadow-2xl"
            style={{ borderRadius: "var(--radius-card)" }}
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="font-display text-2xl uppercase tracking-[0.02em] text-[var(--color-ink-hi)]">
              {t("workoutRunner.leave.title")}
            </h2>
            <p className="mt-2 font-sans text-sm text-[var(--color-ink-mute)]">
              {t("workoutRunner.leave.body", { count: loggedSetsRef.current })}
            </p>
            <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:justify-end">
              <button
                type="button"
                onClick={keepAsUnfinished}
                data-testid="leave-keep"
                className="border border-[var(--color-line-strong)] px-4 py-3 font-subhead text-sm uppercase tracking-[0.06em] text-[var(--color-ink)] hover:bg-[var(--color-slab-2)]"
                style={{ borderRadius: "var(--radius-sharp)", minHeight: "44px" }}
              >
                {t("workoutRunner.leave.keep")}
              </button>
              <button
                type="button"
                onClick={discardWorkout}
                data-testid="leave-discard"
                className="border border-[var(--color-rust)] px-4 py-3 font-subhead text-sm font-bold uppercase tracking-[0.06em] text-[var(--color-rust)] hover:bg-[var(--color-rust)]/10"
                style={{ borderRadius: "var(--radius-sharp)", minHeight: "44px" }}
              >
                {t("workoutRunner.leave.discard")}
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

/** ADR-0013 D5 + F-1: write the synced `user_progress` row for the
 *  exercise the runner just trained, then surface a `level.earned`
 *  toast if the tier rose.
 *
 *  The pre-check is the F-1 fix. The server has **no upsert**: a
 *  second `create` for `(owner, exercise)` is a permanent 400, which
 *  the retry classifier (T-024 DEF-6) never retries — a naive write
 *  here would park an outbox entry that never drains, for any user
 *  who already has the row (i.e. anyone on a second device). So:
 *
 *  1. Look up the local `user_progress` row first. If present, emit
 *     `update` with that id. If absent, emit `create`.
 *  2. The sync engine has a special-case recovery for `create` → 400
 *     with the `data.exercise`/`data.owner` shape: it lists the
 *     owner's row for the exercise, adopts its id, re-emits as
 *     `update`. The pre-check covers the common case; the recovery
 *     covers the remote-only-row case (lost local row, second
 *     device, etc.). The branch is on the response shape, not the
 *     `validation_not_unique` string — error codes have drifted
 *     before.
 *
 *  F-3: the row's `updatedAt` is `now` on every write. The rev-5
 *  stale guard (live, 2026-09-06) requires `updatedAt` ≥ the server
 *  value or the server returns 404. `finishWorkout` writing `now`
 *  satisfies this naturally, including for a resumed workout
 *  finished days later. */
async function writeUserProgressForWorkout(
  exercise: Exercise,
): Promise<{ tier: ProgressionStatus; wasTier: ProgressionStatus; reps: number; seconds: number } | null> {
  const db = getDb();
  const owner = getOwner();
  if (!owner) return null;
  const workouts = (await db.workout_logs
    .where("owner")
    .equals(owner)
    .and((r) => !r.deletedAt && r.completed_at != null)
    .toArray()) as WorkoutLogRow[];
  const entries = (await db.log_entries
    .where("owner")
    .equals(owner)
    .and((r) => !r.deletedAt)
    .toArray()) as LogEntryRow[];
  const stdMap: Record<string, ProgressionStandard> = {
    [exercise.id]: exercise.standard,
  };
  const agg = aggregateProgressWithStandards(workouts, entries, stdMap);
  const cur = agg[exercise.id];
  if (!cur) return null;
  const tier = cur.bestTier;
  const reps = cur.bestReps;
  const seconds = cur.bestHoldSeconds;
  // T-090 R-25 H1: the live `user_progress` server has fields
  // `current_status` (REQUIRED select of locked/beginner/intermediate
  // /mastery) and `unlocked_at`; no `bestTier`. Map the local tier
  // to the server enum (`none` → `locked`) and write `unlocked_at`
  // as the moment the row crossed into the current tier.
  const currentStatus =
    tier === "none" ? "locked" : (tier as "beginner" | "intermediate" | "mastery");
  const local = await db.user_progress
    .where("owner")
    .equals(owner)
    .and((r) => !r.deletedAt && r.exercise === exercise.id)
    .first();
  // The local row's stored tier is read from `current_status` (R-25 H1).
  const existingWasTier = readLocalTier(local);
  const id = local?.id ?? newId();
  const updatedAt = new Date().toISOString();
  const unlockedAt =
    tierRank(tier) > tierRank(existingWasTier) ? updatedAt : (local?.unlocked_at ?? updatedAt);
  await db.transaction(
    "rw",
    [db.user_progress, db.syncQueue],
    async () => {
      const row = {
        id,
        owner,
        exercise: exercise.id,
        current_status: currentStatus,
        unlocked_at: unlockedAt,
        // Local-only fields; the server ignores unknown keys on POST
        // and never sees them on PATCH (LWW on current_status only).
        completedWorkouts: cur.completedWorkouts,
        updatedAt,
        deletedAt: null,
      };
      await db.user_progress.put(row as never);
      await db.syncQueue.add({
        id: newId(),
        collection: "user_progress",
        recordId: id,
        op: local ? "update" : "create",
        payload: JSON.stringify(row),
        attempts: 0,
        status: "pending",
        createdAt: updatedAt,
      });
    },
  );
  // T-095 / orchestrator review: the `calicool:level-earned`
  // dispatch used to live inside this function, fired
  // synchronously after the Dexie transaction. The toast
  // component suppresses itself while a workout is active
  // (a workout was JUST active at this moment, so the event
  // was heard and dropped). The caller (`completeSet`) now
  // schedules the dispatch on a setTimeout(0) AFTER
  // `setPhase("complete")`, so React's commit + the
  // `setWorkoutActive(false)` effect run before the event is
  // heard. We still return the result so the caller can
  // dispatch.
  return { tier, wasTier: existingWasTier, reps, seconds };
}

/** R-25 H1: read the tier stored on a local `user_progress` row.
 *  The row carries `current_status`; pre-H1 rows (in older installs
 *  and the cherry-pick) carry `bestTier`. Read whichever is there. */
function readLocalTier(
  row: { current_status?: string; bestTier?: string } | undefined,
): ProgressionStatus {
  const v = (row?.current_status ?? row?.bestTier ?? "locked") as string;
  if (v === "locked") return "none";
  if (v === "beginner" || v === "intermediate" || v === "mastery") return v;
  return "none";
}

function tierRank(t: ProgressionStatus | undefined): number {
  switch (t) {
    case "mastery":
      return 3;
    case "intermediate":
      return 2;
    case "beginner":
      return 1;
    case "none":
    default:
      return 0;
  }
}

function isHoldFromStandard(std: ProgressionStandard): boolean {
  return (std.holdSeconds ?? 0) > 0;
}